<template>
  <main class="media-detail-page py-16 bg-white min-h-screen">
    <div class="max-w-4xl mx-auto px-4 lg:px-8">
      <!-- 返回按钮 -->
      <div class="mb-6">
        <button
          @click="$router.go(-1)"
          class="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
          </svg>
          {{ $t('common.back') }}
        </button>
      </div>

      <!-- 加载状态 -->
      <div v-if="loading" class="flex justify-center py-12">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>

      <!-- 错误状态 -->
      <div v-else-if="error" class="text-center py-12">
        <p class="text-red-600 mb-4">{{ error }}</p>
        <button
          @click="loadMediaDetail"
          class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          {{ $t('media.retry') }}
        </button>
      </div>

      <!-- 内容 -->
      <article v-else-if="mediaDetail" class="media-article">
        <!-- 头部信息 -->
        <header class="mb-8">
          <div class="flex items-center gap-4 text-sm text-gray-500 mb-4">
            <span>{{ formatDate(mediaDetail.publish_date) }}</span>
            <span v-if="mediaDetail.source">{{ $t('media.source') }}：{{ mediaDetail.source }}</span>
            <span v-if="mediaDetail.view_count">{{ $t('media.views') }}：{{ mediaDetail.view_count }}</span>
          </div>

          <h1 class="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 leading-tight">
            {{ mediaDetail.title }}
          </h1>

          <div v-if="mediaDetail.tags" class="flex flex-wrap gap-2 mb-6">
            <span
              v-for="tag in mediaDetail.tags.split(',')"
              :key="tag.trim()"
              class="px-3 py-1 bg-blue-50 text-blue-600 text-sm rounded-full"
            >
              {{ tag.trim() }}
            </span>
          </div>
        </header>

        <!-- 摘要 -->
        <div v-if="mediaDetail.summary" class="bg-gray-50 p-6 rounded-lg mb-8">
          <h3 class="text-lg font-semibold text-gray-900 mb-3">{{ $t('media.summary') }}</h3>
          <p class="text-gray-700 leading-relaxed">{{ mediaDetail.summary }}</p>
        </div>

        <!-- 正文内容 -->
        <div class="prose prose-lg max-w-none">
          <div v-html="mediaDetail.content"></div>
        </div>

        <!-- 底部信息 -->
        <footer class="mt-12 pt-8 border-t border-gray-200">
          <div class="flex items-center justify-between text-sm text-gray-500">
            <span>{{ $t('media.publishedAt') }} {{ formatDate(mediaDetail.created_at) }}</span>
            <div class="flex items-center gap-4">
              <button
                v-for="lang in languages"
                :key="lang.code"
                @click="switchLanguage(lang.code)"
                :class="[
                  'px-3 py-1 rounded transition-colors',
                  currentLang === lang.code
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                ]"
              >
                {{ lang.name }}
              </button>
            </div>
          </div>
        </footer>
      </article>
    </div>
  </main>
</template>

<script>
export default {
  name: 'MediaDetail',
  data() {
    return {
      mediaDetail: null,
      loading: false,
      error: null,
      currentLang: 'cn',
      languages: [
        { code: 'cn', name: '中文' },
        { code: 'en', name: 'EN' },
        { code: 'tw', name: '繁體' }
      ]
    }
  },
  mounted() {
    // 从URL参数获取语言设置
    const urlParams = new URLSearchParams(window.location.search)
    const lang = urlParams.get('lang')
    if (lang && ['cn', 'en', 'tw'].includes(lang)) {
      this.currentLang = lang
    }
    this.loadMediaDetail()

    // 监听语言切换事件
    window.addEventListener('localeChange', this.onLocaleChange)
  },
  beforeDestroy() {
    window.removeEventListener('localeChange', this.onLocaleChange)
  },
  methods: {
    async loadMediaDetail() {
      try {
        this.loading = true
        this.error = null

        const mediaId = this.$route.params.id
        const response = await fetch(`/api/public/media/${mediaId}?lang=${this.currentLang}`)
        const data = await response.json()

        if (data.code === 0) {
          this.mediaDetail = data.data
        } else {
          throw new Error(data.message || '获取媒体报道详情失败')
        }
      } catch (error) {
        console.error('加载媒体报道详情失败:', error)
        this.error = error.message || '加载媒体报道详情失败'
      } finally {
        this.loading = false
      }
    },

    onLocaleChange(event) {
      // 监听语言切换事件，重新加载数据
      const locale = event.detail.locale
      const mapping = {
        'zh': 'cn',
        'zh-Hant': 'tw',
        'en': 'en'
      }
      const newLang = mapping[locale] || 'cn'
      if (newLang !== this.currentLang) {
        this.currentLang = newLang
        this.$nextTick(() => {
          this.loadMediaDetail()
        })
      }
    },

    async switchLanguage(lang) {
      this.currentLang = lang
      await this.loadMediaDetail()
    },

    formatDate(dateString) {
      const date = new Date(dateString)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')

      const weekdayKeys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']
      const weekdayKey = weekdayKeys[date.getDay()]
      const weekday = this.$t(`weekdays.${weekdayKey}`)

      return `${year}.${month}.${day} ${weekday}`
    }
  }
}
</script>

<style scoped>
.media-detail-page {
  background: #ffffff;
}

.prose {
  color: #374151;
  max-width: none;
}

.prose h1, .prose h2, .prose h3, .prose h4, .prose h5, .prose h6 {
  color: #111827;
  font-weight: 600;
  line-height: 1.25;
  margin-top: 2em;
  margin-bottom: 1em;
}

.prose h1 { font-size: 2.25em; }
.prose h2 { font-size: 1.875em; }
.prose h3 { font-size: 1.5em; }

.prose p {
  margin-top: 1.25em;
  margin-bottom: 1.25em;
  line-height: 1.75;
}

.prose ul, .prose ol {
  margin-top: 1.25em;
  margin-bottom: 1.25em;
  padding-left: 1.625em;
}

.prose li {
  margin-top: 0.5em;
  margin-bottom: 0.5em;
}

.prose blockquote {
  border-left: 4px solid #e5e7eb;
  padding-left: 1em;
  margin: 1.5em 0;
  color: #6b7280;
  font-style: italic;
}

.prose img {
  max-width: 100%;
  height: auto;
  margin: 2em 0;
  border-radius: 0.5em;
}
</style>
